import type { PatchSession } from "./schemas/session.js";

// Who writes each field of a session (#38), and the version of the logger's
// part (#314). One table for the server and the phone.

type EnvelopeField = Exclude<keyof PatchSession, "startedAt">;

/** Who writes each envelope field. `logger`: the logger round-trips it, so
 *  a correction (PUT /sessions/:id/sets) replaces it, and one it leaves out
 *  is removed. `kept`: a correction never touches it. Measured calories, HR
 *  and the Health Connect identity come from enrichment; `fromPlanId` and
 *  `instructions` are not in an edit draft, so removing them would erase
 *  the plan link and the coach's text on every correction. `color` is set
 *  elsewhere. A Record, so a new schema field does not compile until it is
 *  classified here. `startedAt` is the sk and always sent. */
export const SESSION_FIELD_OWNER: Record<EnvelopeField, "logger" | "kept"> = {
  status: "logger",
  endedAt: "logger",
  name: "logger",
  caloriesEstimate: "logger",
  instructions: "kept",
  fromPlanId: "kept",
  color: "kept",
  calories: "kept",
  source: "kept",
  externalId: "kept",
  externalOrigin: "kept",
  avgHrBpm: "kept",
  peakHrBpm: "kept",
};

/** The fields a correction never touches. */
export const SESSION_KEPT_FIELDS = (Object.keys(SESSION_FIELD_OWNER) as EnvelopeField[]).filter(
  (f) => SESSION_FIELD_OWNER[f] === "kept",
);

/**
 * The version a correction is built on and checked against (#314): the
 * athlete's last correction, PATCH of the logger's fields, or set write
 * (`loggerUpdatedAt`). An enrichment from Health Connect leaves it. A session
 * no such write has stamped yet falls back on `updatedAt`, which a write
 * before the field existed did move: an enrichment there may cost a card,
 * never a lost correction.
 */
export const sessionVersion = (s: { loggerUpdatedAt?: string; updatedAt: string }): string =>
  s.loggerUpdatedAt ?? s.updatedAt;
